/**
 * useWorkflowExecution Hook
 *
 * React Query hooks for workflow execution operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workflowsApi } from '../services';
import type {
  WorkflowExecutionResponse,
  WorkflowExecutionListResponse,
  WorkflowExecutionQuery,
} from '../types';

// Execution-specific query keys
export const executionKeys = {
  all: ['workflow-executions'] as const,
  lists: () => [...executionKeys.all, 'list'] as const,
  list: (workflowId: string, filters?: WorkflowExecutionQuery) =>
    [...executionKeys.lists(), workflowId, filters] as const,
  details: () => [...executionKeys.all, 'detail'] as const,
  detail: (id: string) => [...executionKeys.details(), id] as const,
};

/**
 * Hook to fetch execution list for a workflow
 */
export function useWorkflowExecutions(
  workflowId: string | undefined,
  query?: WorkflowExecutionQuery
) {
  return useQuery<WorkflowExecutionListResponse, Error>({
    queryKey: executionKeys.list(workflowId!, query),
    queryFn: () => workflowsApi.listExecutions(workflowId!, query),
    enabled: !!workflowId,
    staleTime: 30 * 1000, // 30 seconds - executions change frequently
    refetchInterval: 10 * 1000, // Auto-refresh every 10 seconds for running executions
  });
}

/**
 * Hook to fetch single execution by ID
 */
export function useWorkflowExecution(executionId: string | undefined) {
  return useQuery<WorkflowExecutionResponse, Error>({
    queryKey: executionKeys.detail(executionId!),
    queryFn: () => workflowsApi.getExecution(executionId!),
    enabled: !!executionId,
    staleTime: 5 * 1000, // 5 seconds - execution details may change rapidly
    refetchInterval: (query) => {
      // Auto-refresh if execution is still running
      const data = query.state.data;
      if (data?.status === 'running' || data?.status === 'pending') {
        return 3 * 1000; // Every 3 seconds while running
      }
      return false; // Stop auto-refresh when completed
    },
  });
}

/**
 * Hook to trigger manual workflow execution
 */
export function useTriggerWorkflowExecution() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowExecutionResponse,
    Error,
    { workflowId: string; context?: Record<string, any> }
  >({
    mutationFn: ({ workflowId, context }) =>
      workflowsApi.triggerManual(workflowId, context),
    onSuccess: (execution, { workflowId }) => {
      // Add the new execution to cache
      queryClient.setQueryData(
        executionKeys.detail(execution.id),
        execution
      );
      // Invalidate execution lists for this workflow
      queryClient.invalidateQueries({
        queryKey: executionKeys.list(workflowId),
      });
    },
  });
}

/**
 * Hook to cancel a running execution
 */
export function useCancelExecution() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowExecutionResponse, Error, string>({
    mutationFn: (executionId) => workflowsApi.cancelExecution(executionId),
    onSuccess: (execution) => {
      // Update the execution in cache
      queryClient.setQueryData(
        executionKeys.detail(execution.id),
        execution
      );
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
    },
  });
}

/**
 * Custom hook for real-time execution monitoring
 *
 * Provides optimized polling for active executions
 */
export function useExecutionMonitor(executionId: string | undefined) {
  const queryClient = useQueryClient();

  const executionQuery = useWorkflowExecution(executionId);

  // Function to manually refresh execution status
  const refresh = () => {
    if (executionId) {
      queryClient.invalidateQueries({
        queryKey: executionKeys.detail(executionId),
      });
    }
  };

  // Check if execution is in a terminal state
  const isTerminal =
    executionQuery.data?.status === 'completed' ||
    executionQuery.data?.status === 'failed' ||
    executionQuery.data?.status === 'cancelled';

  // Check if execution is actively running
  const isRunning =
    executionQuery.data?.status === 'running' ||
    executionQuery.data?.status === 'pending';

  return {
    ...executionQuery,
    refresh,
    isTerminal,
    isRunning,
  };
}
